'use client';

import { Menu, Transition } from '@headlessui/react';
import { cx } from 'cva';
import { ChevronDownIcon } from 'lucide-react';
import React, { Fragment, useState, useRef, useEffect, memo } from 'react';
import Checkbox from '@ui/Checkbox';
import Typography from '@/components/next-ui/Typography';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import { useHomeFeedFilters } from '@/actions/feed.actions';
import Button from '@/components/next-ui/Button';
import PostType from '@/constants/postType.constants';

function SelectFilter({
  icon,
  options,
  placeholder,
  onApply,
  onCancel,
  reset,
}) {
  const setPostTypeFilters = useHomeFeedFilters(
    (state) => state.setPostTypeFilters,
  );
  const postType = useHomeFeedFilters((state) => state.postType);
  const counts = useHomeFeedFilters((state) => state.counts);

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [resultCount, setResultCount] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const isLesserThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useSmaller('md'),
  );

  const handleOptionToggle = (option) => {
    setSelectedOptions((prevSelected) => {
      const isPreviouslySelected = prevSelected.includes(option);
      const updatedOptions = isPreviouslySelected
        ? prevSelected.filter((item) => item !== option)
        : [...prevSelected, option];

      const areTwoFiltersChecked = selectedOptions.some(
        (soption) => soption.parentLabel === option.parentLabel,
      );

      let count = null;

      switch (option.parentLabel) {
        case 'donate':
          if (updatedOptions.some((opt) => opt.parentLabel === 'donate')) {
            if (
              updatedOptions.some((opt) => opt.value === 'fundraisers') &&
              updatedOptions.some((opt) => opt.value === 'inKind')
            ) {
              count = counts['Campaign'];
            } else if (
              updatedOptions.some((opt) => opt.value === 'fundraisers')
            ) {
              count = counts['FundRaiseCounts'];
            } else if (updatedOptions.some((opt) => opt.value === 'inKind')) {
              count = counts['CollectGoodsCounts'];
            } else {
              count = null;
              // count = counts['FundRaiseCounts'];
            }
          }
          break;
        case 'volunteer':
          if (updatedOptions.some((opt) => opt.parentLabel === 'volunteer')) {
            if (
              updatedOptions.some((opt) => opt.value === 'inPerson') &&
              updatedOptions.some((opt) => opt.value === 'virtual')
            ) {
              count = counts['Volunteering'];
            } else if (updatedOptions.some((opt) => opt.value === 'inPerson')) {
              count = counts['VolunteeringInPersonCounts'];
            } else if (updatedOptions.some((opt) => opt.value === 'virtual')) {
              count = counts['VolunteeringVirtualCounts'];
            } else {
              count = null;
              // count = counts['Volunteering'];
            }
          }
          break;
        default:
          count = null;
          break;
      }
      setResultCount(count);

      return updatedOptions;
    });
  };

  const handleReset = () => {
    setSelectedOptions([]);
    setResultCount(null);
  };

  const handleCancel = () => {
    setSelectedOptions([]);
    setResultCount(null);
    if (onCancel) onCancel();
    setIsOpen(false);
  };

  const handleApply = () => {
    setIsOpen(false);

    let donateFilters = {};
    let volunteerFilters = {};
    let nonProfitNewsFilters = false;
    let otherFilters = {};

    selectedOptions.forEach((option) => {
      switch (option.parentLabel) {
        case 'donate':
          donateFilters = { ...donateFilters, [option.value]: true };
          console.log('donateFilters', donateFilters);
          break;
        case 'volunteer':
          volunteerFilters = { ...volunteerFilters, [option.value]: true };
          console.log('volunteerFilters', volunteerFilters);
          break;
        case 'nonProfitNews':
          if (icon) {
            otherFilters = { [option.value]: true };
            console.log('otherFilters', otherFilters);
          }
          break;
        default:
          break;
      }
    });

    const hasFilters = (filters) => Object.entries(filters).length > 0;

    if (hasFilters(donateFilters)) {
      setPostTypeFilters({ donate: donateFilters });
    } else if (hasFilters(volunteerFilters)) {
      setPostTypeFilters({ volunteer: volunteerFilters });
    } else if (hasFilters(otherFilters)) {
      setPostTypeFilters({ others: otherFilters, nonProfitNews: true });
    }
    if (onApply) onApply(selectedOptions);
  };

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (reset) {
      handleReset();
    }
  }, [reset]);

  useEffect(() => {
    if (isLesserThanMediumScreen) {
      if (postType === 'all') {
        sessionStorage.removeItem('home-filter');
        handleReset();
      } else {
        let filteredOptions = [];

        if (postType === PostType.Campaign) {
          filteredOptions = options.filter(
            (option) => option.parentLabel === 'donate',
          );
        } else if (postType === PostType.Volunteering) {
          filteredOptions = options.filter(
            (option) => option.parentLabel === 'volunteer',
          );
        }
        filteredOptions.forEach((option) => {
          handleOptionToggle(option);
        });
      }
    }
  }, [postType]);

  return (
    <Menu
      as="div"
      className={
        (cx('inline-block cursor-pointer text-left'),
        isLesserThanMediumScreen ? '' : 'relative')
      }
      ref={menuRef}>
      <div>
        <Menu.Button
          onClick={toggleMenu}
          className={cx(
            'relative inline-flex w-full justify-center rounded-lg py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75',
            selectedOptions.length > 0
              ? 'bg-chips-primary'
              : 'bg-primary-light-1',
            icon ? 'px-3' : 'px-2',
            isLesserThanMediumScreen ? 'px-2' : 'px-6',
          )}>
          <div className="flex items-center">
            {icon ? (
              <div className="flex items-center justify-center text-text-disabled">
                {icon}
              </div>
            ) : (
              <Typography variant="body1" color="textPrimary">
                {placeholder}
              </Typography>
            )}
            {!icon && (
              <ChevronDownIcon
                className="ml-2 h-4 w-4 text-text-secondary transition duration-150 ease-in-out"
                aria-hidden="true"
              />
            )}
          </div>

          <div
            className={cx(
              selectedOptions.length === 0 && 'hidden',
              'absolute left-0 top-0 flex h-5 min-w-[20px] -translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full bg-chips-primary-dark p-1.5 text-center text-13 font-medium leading-none text-white',
            )}>
            {selectedOptions.length}
          </div>
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        show={isOpen}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95">
        <Menu.Items
          className={cx(
            'absolute z-3000 mt-2 max-h-[500px] origin-top-right divide-y divide-gray-100 overflow-y-auto rounded-xl bg-white shadow-xl ring-1 ring-black ring-opacity-10 focus:outline-none sm:min-w-0 md:w-max',
            isLesserThanMediumScreen
              ? 'left-2 right-4 mx-auto w-[80%]'
              : 'bottom-full left-0 right-auto min-w-[calc(100vw*0.7)]',
          )}>
          <div className="relative z-3000 flex flex-col gap-2">
            <div className="relative z-3000 h-0.5 w-full bg-body-background" />
            {options.map((option) => (
              <Menu.Item key={option.value} as="div" className="py-2 pl-4 pr-4">
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => handleOptionToggle(option)}
                    className={cx(
                      'z-3000 flex w-full select-none items-center gap-4 text-left text-16',
                      active && 'bg-primary-light-1',
                      active || selectedOptions.includes(option)
                        ? 'text-text-primary'
                        : 'text-text-secondary',
                    )}>
                    <div className="flex items-center justify-center">
                      <Checkbox
                        className="border-text-disabled font-extrabold"
                        checked={selectedOptions.includes(option)}
                        size={26}
                      />
                    </div>
                    {option.label}
                  </button>
                )}
              </Menu.Item>
            ))}
            <div className="my-2 border-t border-gray-200 py-2">
              <div className="flex justify-end px-2">
                <Button variant="text" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="filled" onClick={handleApply}>
                  Show {resultCount >= 0 ? resultCount : ''} results
                </Button>
              </div>
            </div>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

export default React.memo(SelectFilter);
